/**
 * Admin "AI training" page — `/ops/ai-training`.
 *
 * Lists EventDecisions where an admin set the `flaggedForAiTraining` bit.
 * Two buckets:
 *  - **Flagged rejections** — events the AI pipeline (noise filters +
 *    extractor prompt + judge) should have rejected automatically.
 *  - **Flagged approvals** — events the pipeline should have been
 *    confident enough to auto-publish (skip the review queue) by raising
 *    the auto-publish threshold or improving the extractor prompt.
 *
 * Each row also surfaces `eventCreatedAt` so reviewers can spot flags on
 * events that were extracted BEFORE a known pipeline improvement (those
 * are already fixed by newer code and can be ignored).
 *
 * Accessibility:
 * - One `<h1>` (the page heading).
 * - Each bucket uses its own `<section>` with a labeled `<h2>` and a table
 *   with `<caption>` + `<th scope="col">`.
 * - Long input previews are truncated server-side; full text shown via
 *   `<details>`.
 */

import { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { LoadingBlock } from '../../components/Spinner';
import { api, isAuthPending } from '../../lib/api';

type DecisionType = 'approved' | 'rejected' | 'edited';

interface FlaggedItem {
  partitionKey: string;
  rowKey: string;
  decision: DecisionType;
  decidedAt: string;
  decidedBy?: string;
  eventRowKey: string;
  eventPartitionKey: string;
  /** ISO timestamp of the originating ExtractedEvent at decision time. */
  eventCreatedAt?: string;
  rawRowKey?: string;
  rejectionReason?: string;
  modelConfidence?: number;
  hadImage?: boolean;
  inputSourceUrl?: string;
  inputHint?: string;
  inputTextPreview?: string;
  originalTitle?: string;
  originalStartIso?: string;
}

interface FlaggedResponse {
  count: number;
  returned: number;
  items: FlaggedItem[];
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

interface BucketTableProps {
  items: FlaggedItem[];
  /** Column header for the per-decision "reason / signal" column. */
  reasonHeader: string;
}

function BucketTable({ items, reasonHeader }: BucketTableProps) {
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle">
        <caption className="text-muted small">
          Admin-flagged decisions sorted by most recent first. <strong>Event
          created</strong> is when the AI pipeline first produced the event —
          flags on events created before a pipeline improvement may already
          be handled by newer code.
        </caption>
        <thead>
          <tr>
            <th scope="col">Decided</th>
            <th scope="col">Event created</th>
            <th scope="col">Event title</th>
            <th scope="col">Event start</th>
            <th scope="col">{reasonHeader}</th>
            <th scope="col">Source</th>
            <th scope="col">Model confidence</th>
            <th scope="col">Decided by</th>
            <th scope="col">Input preview</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={`${it.partitionKey}|${it.rowKey}`}>
              <td className="text-nowrap small">{formatTime(it.decidedAt)}</td>
              <td className="text-nowrap small">
                {it.eventCreatedAt ? formatTime(it.eventCreatedAt) : <span className="text-muted">—</span>}
              </td>
              <td>{it.originalTitle ?? <span className="text-muted">—</span>}</td>
              <td className="text-nowrap small">
                {it.originalStartIso ? formatTime(it.originalStartIso) : <span className="text-muted">—</span>}
              </td>
              <td className="small">{it.rejectionReason ?? <span className="text-muted">—</span>}</td>
              <td className="small">
                {it.inputSourceUrl ? (
                  <a href={it.inputSourceUrl} target="_blank" rel="noopener noreferrer">
                    link
                  </a>
                ) : (
                  <span className="text-muted">—</span>
                )}
                {it.hadImage && (
                  <span
                    className="badge text-bg-light ms-1"
                    title="Original raw post had an image"
                  >
                    img
                  </span>
                )}
              </td>
              <td className="small">
                {typeof it.modelConfidence === 'number'
                  ? `${Math.round(it.modelConfidence * 100)}%`
                  : '—'}
              </td>
              <td className="small">{it.decidedBy ?? <span className="text-muted">—</span>}</td>
              <td className="small">
                {it.inputTextPreview ? (
                  <details>
                    <summary>view</summary>
                    <pre className="small mh-prewrap mb-0">{it.inputTextPreview}</pre>
                  </details>
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminAiTrainingPage() {
  const [data, setData] = useState<FlaggedResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<FlaggedResponse>('/api/mgmt/events/ai-training-flagged?limit=500')
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((err) => {
        if (cancelled) return;
        if (!isAuthPending(err)) setError(String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { rejections, approvals } = useMemo(() => {
    const items = data?.items ?? [];
    return {
      rejections: items.filter((i) => i.decision === 'rejected'),
      approvals: items.filter((i) => i.decision === 'approved'),
    };
  }, [data]);

  return (
    <AdminLayout>
      <h1 className="h3 mb-3">AI training — flagged decisions</h1>
      <p className="text-muted">
        Decisions you flagged on the review queue / list views as ones the
        AI pipeline should have made automatically. Use the
        <strong> rejections</strong> bucket to tighten
        <code> lib/noise-filters.ts</code>, the LLM judge, and the few-shot
        REJECT examples in the extractor prompt. Use the
        <strong> approvals</strong> bucket to tune the auto-publish
        confidence threshold (see <code>autoPublishConfidence</code> in
        Settings) and the extractor prompt so high-quality events skip the
        queue.
      </p>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {loading && <LoadingBlock label="Loading flagged decisions…" />}

      {!loading && data && (
        <>
          <p className="text-muted small">
            Showing {data.returned} of {data.count} flagged decisions
            ({approvals.length} approval{approvals.length === 1 ? '' : 's'},{' '}
            {rejections.length} rejection{rejections.length === 1 ? '' : 's'})
            {data.count > data.returned ? ' — most recent first; raise limit by editing the URL.' : '.'}
          </p>

          <section aria-labelledby="flagged-approvals-heading" className="mb-4">
            <h2 id="flagged-approvals-heading" className="h5">
              Flagged manual approvals
              <span className="badge text-bg-success ms-2">{approvals.length}</span>
            </h2>
            <p className="text-muted small">
              The AI should have been confident enough to auto-publish these
              without a human in the loop. Look for shared signals
              (organizer, source, event type) you can promote in the
              extractor prompt or use to lower the
              <code> autoPublishConfidence</code> threshold safely.
            </p>
            {approvals.length === 0 ? (
              <div className="alert alert-info" role="status">
                No flagged approvals yet. On the <em>Review queue</em>, choose
                <em> &ldquo;Approve &amp; flag for AI auto-approval&rdquo;</em>
                {' '}to send an approval here.
              </div>
            ) : (
              <BucketTable items={approvals} reasonHeader="Note" />
            )}
          </section>

          <section aria-labelledby="flagged-rejections-heading">
            <h2 id="flagged-rejections-heading" className="h5">
              Flagged rejections
              <span className="badge text-bg-danger ms-2">{rejections.length}</span>
            </h2>
            <p className="text-muted small">
              The AI should have rejected these upstream. Each row is a
              candidate to add to <code>lib/noise-filters.ts</code>, the
              LLM judge, or the few-shot REJECT examples in the extractor
              prompt.
            </p>
            {rejections.length === 0 ? (
              <div className="alert alert-info" role="status">
                No flagged rejections yet. When you hide an event and choose
                <em> &ldquo;Reject &amp; flag for AI training&rdquo;</em>, it
                shows up here.
              </div>
            ) : (
              <BucketTable items={rejections} reasonHeader="Reject reason" />
            )}
          </section>
        </>
      )}
    </AdminLayout>
  );
}
